import React from 'react';
import { WorkOrder } from '../../types/workOrder';
import { Badge } from '../ui/Badge';
import { Button } from '../ui/Button';
import { SLABadge } from '../sla/SLABadge';
import { Eye, Clock, DollarSign, Wrench } from 'lucide-react';

interface WorkOrderTableProps {
  workOrders: WorkOrder[];
  onSelect: (wo: WorkOrder) => void;
}

export const WorkOrderTable: React.FC<WorkOrderTableProps> = ({ workOrders, onSelect }) => {
  if (workOrders.length === 0) {
    return (
      <div className="p-8 text-center bg-slate-900 border border-slate-800 rounded-xl text-slate-400">
        No active work orders found matching search criteria.
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {workOrders.map(wo => (
        <div key={wo.work_order_id} className="bg-slate-900 border border-slate-800 rounded-xl p-5 space-y-4 hover:border-slate-700 transition-all">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="font-mono text-xs font-semibold text-sky-400">{wo.work_order_id}</span>
              <span className="text-[10px] text-slate-500 font-mono">({wo.complaint_id})</span>
            </div>
            <div className="flex items-center gap-2">
              <Badge label={wo.status} variant={wo.status === 'In Progress' ? 'warning' : 'info'} />
              {wo.status !== 'Completed' && wo.status !== 'Inspected' && (
                <SLABadge createdAt={wo.created_at} priorityName="Medium" completedAt={wo.completion_date} compact />
              )}
            </div>
          </div>

          <div>
            <h4 className="font-semibold text-slate-100 text-sm leading-snug">{wo.description}</h4>
            <p className="text-xs text-slate-400 mt-1 line-clamp-2">{wo.scope_of_work}</p>
          </div>

          <div className="grid grid-cols-2 gap-2 text-xs bg-slate-950 p-3 rounded-lg border border-slate-800/80">
            <div>
              <p className="text-[10px] text-slate-500 font-medium">Assigned Vendor</p>
              <p className="font-semibold text-slate-200 truncate">{wo.vendor_name || 'Unassigned'}</p>
            </div>
            <div>
              <p className="text-[10px] text-slate-500 font-medium">Est. Cost</p>
              <p className="font-semibold text-emerald-400">${wo.estimated_cost}</p>
            </div>
          </div>

          <div className="flex items-center justify-between text-xs pt-1 border-t border-slate-800">
            <span className="text-slate-500 flex items-center gap-1">
              <Clock className="w-3.5 h-3.5" /> Target: {wo.target_date}
            </span>
            <Button size="sm" variant="ghost" onClick={() => onSelect(wo)} icon={<Eye className="w-3.5 h-3.5" />}>
              Details
            </Button>
          </div>
        </div>
      ))}
    </div>
  );
};
